import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Box, Stack, Typography, Card, Pagination } from "@mui/material";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import { useSetRecoilState, useRecoilValue } from "recoil";
import { useSnackbar } from "notistack";

import ReusableToggleBtns from "../Components/Reusable/ReusableToggleBtns";
import CardSearch from "../Components/Search/CardSearch";
import GridContainer from "../Components/HOC/GridContainer";
import SkeletonLoaderReusable from "../Components/Reusable/SkeletonLoaderReusable";
import TableReusable from "../Components/Reusable/TableReusable";
import DesBtn from "../Components/Reusable/DesBtn";
import PredictionFilter from "../Components/PopUp/Filter";

import {
  dataRenderTypeInSearchArr,
  searchFramesColumns,
  baseURL,
} from "../utils/StaticVariables";
import { popupState, authState } from "../Recoil/RecoilState";

// 🛠 Helper Function
const buildParams = ({ filter, page, limit }) => ({
  ...filter,
  end_time: filter.endTime,
  start_time: filter.startTime,
  start_date: filter.startDate,
  end_date: filter.endDate,
  camera_id: filter.camera_id,
  buildings: filter.buildings,
  zones: filter.zones,
  areas: filter.areas,
  locations: filter.locations,
  page,
  per_page: limit,
});

export default function Search() {
  const setPopup = useSetRecoilState(popupState);
  const { token } = useRecoilValue(authState);
  const { enqueueSnackbar } = useSnackbar();
  const childRef = useRef(null);

  // 🔹 Filter state
  const [filter, setFilter] = useState({});
  const [page, setPage] = useState(1);
  const [limit] = useState(8);
  const [viewMode, setViewMode] = useState("cards");

  // 🔹 Data states
  const [paginated, setPaginated] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(0);

  // 🔹 Loading state
  const [loading, setLoading] = useState(true);

  // 🔹 Locations Hierarchy state
  const [hierarchy, setHierarchy] = useState({
    buildings: [],
    zones: [],
    areas: [],
    locations: [],
  });

  // 📌 Fetch Locations Hierarchy once
  useEffect(() => {
    const fetchHierarchy = async () => {
      try {
        const { data } = await axios.get(`${baseURL}locations/hierarchy`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setHierarchy(data);
      } catch (err) {
        console.error("Hierarchy fetch error:", err);
        enqueueSnackbar("Failed to load locations hierarchy", { variant: "error" });
      }
    };
    fetchHierarchy();
  }, [token, enqueueSnackbar]);

  // 📌 Fetch Data
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);

      const params = buildParams({ filter, page, limit });
      const headers = { Authorization: `Bearer ${token}` };

      try {
        if (viewMode === "cards") {
          const pageRes = await axios.get(`${baseURL}workspace/search_results_with_location`, { 
            params, 
            headers 
          });
          const { data: pageData, total_count, num_of_pages } = pageRes.data;
          setPaginated(pageData || []);
          setTotal(total_count || 0);
          setPages(num_of_pages || 0);
          setLoading(false);
        } 
        else if (viewMode === "table") {
          const pageRes = await axios.get(`${baseURL}workspace/search_results_with_location`, {
            params: { ...params, base64: false },
            headers,
          });
          const { data: pageData, total_count, num_of_pages } = pageRes.data;
          setPaginated(pageData || []);
          setTotal(total_count || 0);
          setPages(num_of_pages || 0);
          setLoading(false);
        }
      } catch (err) {
        console.error("Data fetch error:", err);
        enqueueSnackbar(
          err.response?.data?.message || "Failed to fetch data", 
          { variant: "error" }
        );
        setLoading(false);
      }
    };

    fetchData();
  }, [filter, page, limit, token, viewMode, enqueueSnackbar]);

  // 📌 Change View
  const onViewChange = (_, mode) => {
    if (mode) {
      setViewMode(mode);
      setPage(1); // Reset to first page when changing view mode
    }
  };

  // 📌 Open Filter Popup with hierarchy data
  const openFilter = () => {
    setPopup({
      isOpen: true,
      title: "Select Date, Time and Location Filters",
      content: (
        <PredictionFilter
          ref={childRef}
          filter={filter}
          changeFilterHandle={setFilter}
          hierarchy={hierarchy}
        />
      ),
      sendReq: () => childRef.current?.submit(),
    });
  };

  // 📌 No results warning
  useEffect(() => {
    if (!loading && paginated.length === 0 && total === 0) {
      enqueueSnackbar("No results found", { variant: "warning" });
    }
  }, [loading, paginated, total, enqueueSnackbar]);

  return (
    <Box>
      {!loading && (
        <Stack my={4} spacing={2}>
          <Typography variant="body1" textAlign={{ xs: "center", md: "left" }}>
            Available Recorded Frames: <strong>{total}</strong>
          </Typography>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            justifyContent="space-between"
            alignItems="center"
          >
            <Box flex={1}>
              <ReusableToggleBtns
                options={dataRenderTypeInSearchArr}
                value={viewMode}
                handleToggleChange={onViewChange}
              />
            </Box>

            <Stack direction="row" spacing={2}>
              <DesBtn text="Filter" handle={openFilter}>
                <FilterAltOutlinedIcon />
              </DesBtn>
            </Stack>
          </Stack>
        </Stack>
      )}

      {/* Cards View */}
      {viewMode === "cards" &&
        (loading ? (
          <SkeletonLoaderReusable />
        ) : paginated.length ? (
          <GridContainer
            items={paginated.map((el) => (
              <CardSearch key={el.frame || el.id} data={el} />
            ))}
          />
        ) : (
          <Card sx={{ p: 3, my: 4, textAlign: "center" }}>
            No Data Available
          </Card>
        ))}

      {/* Table View */}
      {viewMode === "table" && (
        <TableReusable
          print
          data={paginated}
          columns={searchFramesColumns}
          loading={loading}
          page={page}
          count={total}
        />
      )}

      {/* Pagination */}
      {!loading && pages > 1 && (
        <Stack justifyContent="center" sx={{ mt: 4 }}>
          <Pagination
            count={pages}
            page={page}
            onChange={(_, p) => setPage(p)}
          />
        </Stack>
      )}
    </Box>
  );
}